// Soft-deactivate the workspace organization (no hard delete). Server enforces:
// org owner, manufacturer owner/admin for this subscriber, or platform staff.
import { useCallback, useEffect, useState } from "react";
import { requireSupabase } from "@/lib/supabase";
import { getOrgLifecycleDeactivateEligibility, postOrgDeactivate } from "@/lib/adsAssistantClient";

type Props = {
  organizationId: string;
  orgName: string | null;
  activeMemberCount: number;
  isPlatformAdminOrDev: boolean;
  onDeactivated?: () => void;
};

export function OrgDeactivateSection({
  organizationId,
  orgName,
  activeMemberCount,
  isPlatformAdminOrDev,
  onDeactivated,
}: Props) {
  const sb = requireSupabase();
  const [canDeactivate, setCanDeactivate] = useState<boolean | null>(null);
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [transferToOrgId, setTransferToOrgId] = useState("");
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const refreshEligibility = useCallback(() => {
    setCanDeactivate(null);
    void getOrgLifecycleDeactivateEligibility().then((r) => {
      if (!r.ok) {
        setCanDeactivate(false);
        return;
      }
      const d = r.data as { ok?: boolean; can_deactivate?: boolean };
      setCanDeactivate(d?.can_deactivate === true);
    });
  }, []);

  useEffect(() => {
    refreshEligibility();
  }, [organizationId, refreshEligibility]);

  useEffect(() => {
    // Listing all orgs uses a platform RPC that may not be granted to every platform role.
    // Skip this fetch unless the section is enabled for true platform-admin contexts.
    if (!isPlatformAdminOrDev) return;
    void sb.rpc("platform_list_orgs").then(({ data, error }) => {
      if (error || !data) return;
      const rows = (data as { id: string; name: string }[]).map((o) => ({
        id: o.id,
        name: o.name ?? o.id,
      }));
      setOrgs(rows.filter((o) => o.id !== organizationId).sort((a, b) => a.name.localeCompare(b.name)));
    });
  }, [isPlatformAdminOrDev, organizationId, sb]);

  async function submitDeactivate() {
    const label = orgName?.trim() || "this organization";
    const transfer = transferToOrgId.trim() || null;
    let confirmText: string;
    if (isPlatformAdminOrDev && transfer) {
      const dest = orgs.find((o) => o.id === transfer)?.name ?? transfer;
      confirmText = `Deactivate “${label}” and move active members to “${dest}”?`;
    } else if (activeMemberCount > 0) {
      confirmText = `Deactivate “${label}”? It has ${activeMemberCount} active member(s). They will keep role rows but the org will be marked inactive. Continue?`;
    } else {
      confirmText = `Deactivate “${label}”? The organization will be marked inactive (not permanently deleted). Continue?`;
    }
    if (!window.confirm(confirmText)) return;

    setWorking(true);
    setMsg(null);
    try {
      const r = await postOrgDeactivate({
        transfer_to_organization_id: isPlatformAdminOrDev ? transfer : null,
      });
      if (!r.ok) {
        setMsg({ text: r.error, ok: false });
        return;
      }
      setMsg({ text: "Organization deactivated.", ok: true });
      onDeactivated?.();
    } finally {
      setWorking(false);
    }
  }

  if (canDeactivate === null) {
    return <p className="text-xs text-gray-400">Checking deactivation permissions…</p>;
  }
  if (!canDeactivate) {
    return null;
  }

  return (
    <div className="rounded-xl border border-red-200 bg-red-50/80 dark:bg-red-950/20 dark:border-red-900/60 p-4 space-y-3">
      <h3 className="text-sm font-semibold text-red-900 dark:text-red-200">Deactivate organization</h3>
      <p className="text-xs text-red-800/90 dark:text-red-200/80">
        Marks this org inactive in the database (same as platform “deactivate org”). Hard deletes are a separate
        database upkeep task. You can do this if you are the org owner, a manufacturer owner/admin for this dealer, or
        platform staff.
      </p>
      {msg ? (
        <div
          className={`text-sm rounded-lg px-3 py-2 border ${
            msg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white border-red-200 text-red-800"
          }`}
        >
          {msg.text}
        </div>
      ) : null}
      {isPlatformAdminOrDev ? (
        <label className="block text-xs">
          <span className="font-medium text-red-900 dark:text-red-200">Optional: transfer members to</span>
          <select
            className="mt-1 w-full max-w-md text-sm border border-red-200 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 dark:border-red-900"
            value={transferToOrgId}
            onChange={(e) => setTransferToOrgId(e.target.value)}
            disabled={working}
          >
            <option value="">— No transfer (members stay assigned to this org id) —</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <span className="text-[10px] text-red-700/80 dark:text-red-300/80 mt-1 block">
            Uses platform <span className="font-mono">platform_deactivate_org</span> when a target is selected or when
            you are platform staff.
          </span>
        </label>
      ) : null}
      <button
        type="button"
        disabled={working}
        onClick={() => void submitDeactivate()}
        className="px-3 py-2 rounded-lg bg-red-700 text-white text-sm hover:bg-red-800 disabled:opacity-50"
      >
        {working ? "Working…" : "Deactivate organization…"}
      </button>
    </div>
  );
}
